import React from 'react';
import '../css/SalesTable.css';

/**
 * SalesTable Component
 * Displays sales data in a table format with monthly breakdowns
 * Includes CSV export functionality
 * 
 * @param {Object[]} salesData - Array of sales records with monthly data
 */
const SalesTable = ({ salesData, company, periodDate, dateRange }) => {
  // Early return if no data available
  if (!salesData || salesData.length === 0) {
    return <p>No sales data available</p>;
  }
  
  // Extract months data from the first record
  const months = salesData[0]?.months ? Object.values(salesData[0].months) : [];

  periodDate = periodDate.replace(/-/g, '');

  /**
   * Generates and downloads CSV file of sales data
   * Includes monthly breakdowns of costs and retail values
   */
  const downloadCSV = () => {
    // Define CSV headers
    const headers = [
      'Company',
      'Location',
      'Class Code',
      'Period',
      'Sales',
      'Markdown',
      'Inventory',
      'InventoryAdjustments',
      'ReceivingCost',
      'ReceivingRetail',
      'TransfersIn',
      'TransfersOut',
      'ReturnsRetail',
      'ReturnsCost',
      ...months.flatMap((month, index) => [
        `OnOrderCost${index === 0 ? '' : index}`, 
        `OnOrderRetail${index === 0 ? '' : index}`
      ])
    ];

    // Generate CSV data rows
    const csvData = salesData.map(item => {
      const monthData = Object.values(item.months).flatMap(month => [
        month.totalCost.toFixed(2),
        month.totalRetail.toFixed(2)
      ]);

      return [
        company,
        item.location, // Use item.location from backend
        item.classCode,
        periodDate,
        item.totalSales.toFixed(2),
        item.totalDiscounts.toFixed(2),
        item.inventory.toFixed(2),
        '',  // InventoryAdjustments
        item.receivingCost.toFixed(2),
        item.receivingRetail.toFixed(2),
        '',  // TransfersIn
        '',  // TransfersOut
        '',  // ReturnsRetail
        '',  // ReturnsCost
        ...monthData
      ];
    });

    // Create and trigger CSV download
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    const formatFileName = () => {
      const sanitizedCompany = company.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const sanitizedDate = dateRange;
      return `${sanitizedCompany}_${sanitizedDate}.txt`;
    };

    link.download = formatFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="sales-table-container">
      {/* Table header with title and download button */}
      <div className="table-header">
        <h3>SALES REPORT</h3>
        <button 
          onClick={downloadCSV} 
          className="download-button"
          aria-label="Download sales report as CSV"
        >
          Download File
        </button>
      </div>

      {/* Table wrapper for horizontal scrolling */}
      <div className="table-wrapper">
        <table className="sales-table">
          <thead>
            {/* Main header row */}
            <tr>
              <th>Company</th>
              <th>Location</th>
              <th>Class Code</th>
              <th>Period</th>
              <th>Sales</th>
              <th>Markdown</th>
              <th>Inventory</th>
              <th>InventoryAdjustments</th>
              <th>ReceivingCost</th>
              <th>ReceivingRetail</th>
              <th>TransfersIn</th>
              <th>TransfersOut</th>
              <th>ReturnsRetail</th>
              <th>ReturnsCost</th>
              {months.map((month) => (
                <th key={month.date} colSpan="2">{month.date}</th>
              ))}
            </tr>
            {/* Subheader row for month columns */}
            <tr>
              <th colSpan="14"></th>
              {months.map((month) => (
                <React.Fragment key={`header-${month.date}`}>
                  <th>Cost</th>
                  <th>Retail</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Data rows */}
            {salesData.map((item) => (
              <tr key={`${item.location}-${item.classCode}`}>
                <td>{company}</td>
                <td>{item.location}</td>
                <td>{item.classCode}</td>
                <td>{periodDate}</td>
                <td>${item.totalSales.toFixed(2)}</td>
                <td>${item.totalDiscounts.toFixed(2)}</td>
                <td>${item.inventory.toFixed(2)}</td>
                <td></td>
                <td>${item.receivingCost.toFixed(2)}</td>
                <td>${item.receivingRetail.toFixed(2)}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                {Object.values(item.months).map((month) => (
                  <React.Fragment key={`${item.location}-${item.classCode}-${month.date}`}>
                    <td>${month.totalCost.toFixed(2)}</td>
                    <td>${month.totalRetail.toFixed(2)}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesTable;
